import {
  Box,
  Button,
  Group,
  TextInput,
  Textarea,
  ActionIcon,
  Text,
  Badge,
} from "@mantine/core";
import Icon from "../../common/Icons/Icons/Icon";
import { Theme } from "src/interfaces/KnowledgeBase";

type NestedFormProps = {
  data: {
    config: any;
    themes: Theme[];
    addTheme: () => void;
    removeTheme: (themeIndex: number) => void;
    updateThemeName: (themeIndex: number, name: string) => void;
    addTopic: (themeIndex: number) => void;
    removeTopic: (themeIndex: number, topicIndex: number) => void;
    updateTopicName: (
      themeIndex: number,
      topicIndex: number,
      name: string
    ) => void;
    addLearningObjective: (themeIndex: number, topicIndex: number) => void;
    removeLearningObjective: (
      themeIndex: number,
      topicIndex: number,
      loIndex: number
    ) => void;
    updateLearningObjectiveName: (
      themeIndex: number,
      topicIndex: number,
      loIndex: number,
      name: string
    ) => void;
    updateContent: (
      themeIndex: number,
      topicIndex: number,
      loIndex: number,
      content: string
    ) => void;
  };
};

export const ManualUploadKnowledgeBaseEditor = ({ data }: NestedFormProps) => {
  const {
    config,
    themes,
    addTheme,
    removeTheme,
    updateThemeName,
    addTopic,
    removeTopic,
    updateTopicName,
    addLearningObjective,
    removeLearningObjective,
    updateLearningObjectiveName,
    updateContent,
  } = data;

  if (!config) {
    return (
      <Text size="sm" color="dimmed">
        No configuration available.
      </Text>
    );
  }

  return (
    <Box className="pr-3">
      <div className="flex justify-end">
        <Button variant="light" size="xs" radius="xl" onClick={addTheme}>
          <Icon name="plus" size="16px" className="mr-1" /> Add{" "}
          {config.themeLabel}
        </Button>
      </div>
      {themes.map((theme, themeIndex) => (
        <Box
          key={themeIndex}
          p="md"
          mt="md"
          className="bg-gray-50 rounded-xl shadow-sm"
        >
          <Group
            position="apart"
            className="flex justify-between bg-white p-2 items-end rounded-lg relative"
          >
            <Badge className="py-[13px] px-2 rounded border text-sm">
              {themeIndex + 1}
            </Badge>
            <TextInput
              label={config.themeLabel}
              size="xs"
              value={theme.name}
              onChange={(e) => updateThemeName(themeIndex, e.target.value)}
              required
              style={{ flex: 1 }}
            />
            {themes.length > 1 && (
              <ActionIcon color="red" onClick={() => removeTheme(themeIndex)}>
                <Icon name="trash" size="18px" />
              </ActionIcon>
            )}
          </Group>
          <div className="flex justify-end my-3">
            <Button
              variant="light"
              size="xs"
              radius="xl"
              onClick={() => addTopic(themeIndex)}
            >
              <Icon name="plus" size="16px" className="mr-1" /> Add{" "}
              {config.topicLabel}
            </Button>
          </div>
          {/* Topics */}
          <Box className="bg-gray-100 py-3 pl-8 sm:pl-0 pr-3 rounded-lg sm:ml-0 ml-8">
            {theme.topics.map((topic, topicIndex) => (
              <Box
                key={topicIndex}
                className={
                  topicIndex !== 0 ? "border-t-2 border-gray-200 pt-2 mt-2" : ""
                }
              >
                <Group
                  position="apart"
                  className="flex justify-between bg-white p-2 items-end rounded-lg"
                >
                  <Badge className="py-[13px] px-2 rounded border text-sm">
                    {topicIndex + 1}
                  </Badge>
                  <TextInput
                    label={config.topicLabel}
                    size="xs"
                    value={topic.name}
                    onChange={(e) =>
                      updateTopicName(themeIndex, topicIndex, e.target.value)
                    }
                    required
                    style={{ flex: 1 }}
                  />
                  {theme.topics.length > 1 && (
                    <ActionIcon
                      color="red"
                      onClick={() => removeTopic(themeIndex, topicIndex)}
                    >
                      <Icon name="trash" size="18px" />
                    </ActionIcon>
                  )}
                </Group>

                {/* Learning Objectives */}
                <div className="flex justify-end my-3">
                  <Button
                    variant="light"
                    size="xs"
                    radius="xl"
                    onClick={() => addLearningObjective(themeIndex, topicIndex)}
                  >
                    <Icon name="plus" size="16px" className="mr-1" /> Add{" "}
                    {config.learningObjectiveLabel}
                  </Button>
                </div>
                <Box className="bg-white p-2 rounded-lg pr-3 sm:ml-0 ml-8">
                  {topic.learning_objectives.map((lo, loIndex) => (
                    <Box
                      key={loIndex}
                      mt="sm"
                      className={
                        loIndex !== 0
                          ? "border-t-2 border-gray-100 pt-1 pb-2 px-2 bg-gray-50 rounded-lg"
                          : "bg-gray-50 p-2 rounded-lg"
                      }
                    >
                      <Group
                        position="apart"
                        className="flex items-end  px-2 rounded-lg"
                      >
                        <Badge className="py-[13px] px-2 rounded border text-sm">
                          {loIndex + 1}
                        </Badge>
                        <TextInput
                          label={config.learningObjectiveLabel}
                          size="xs"
                          className="bg-transparent"
                          value={lo.name}
                          onChange={(e) =>
                            updateLearningObjectiveName(
                              themeIndex,
                              topicIndex,
                              loIndex,
                              e.target.value
                            )
                          }
                          required
                          style={{ flex: 1 }}
                        />
                        {topic.learning_objectives.length > 1 && (
                          <ActionIcon
                            color="red"
                            onClick={() =>
                              removeLearningObjective(
                                themeIndex,
                                topicIndex,
                                loIndex
                              )
                            }
                          >
                            <Icon name="trash" size="18px" />
                          </ActionIcon>
                        )}
                      </Group>
                      {config.contentLabel && (
                        <Textarea
                          label={config.contentLabel}
                          size="xs"
                          className="sm:ml-0 ml-8"
                          placeholder={config.contentPlaceholder}
                          value={lo.content}
                          onChange={(e) =>
                            updateContent(
                              themeIndex,
                              topicIndex,
                              loIndex,
                              e.target.value
                            )
                          }
                          required
                          mt="xs"
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
